// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { BankAccount } from '@client/banks/types/account';
import type { BankUploadTemplateEntity } from '@client/banks/utils/download-bank-upload-template';
import { hasLinkableAccountIban } from '@client/banks/utils/account-reference';
import { normalizeIban } from '@client/banks/utils/bank-utils';
import { isUndefined } from '@shared/utils';

/**
 * Entities that may link to a liquidity account IBAN. When the cell is empty,
 * linking is optional (backend creates a currency-based link account).
 */
export const BANK_UPLOAD_ENTITIES_REQUIRING_ACCOUNT_IBAN: ReadonlySet<BankUploadTemplateEntity> =
  new Set(['positions', 'transactions', 'debts']);

export const normalizeBankUploadAccountIban = (
  iban: string | null | undefined,
): string => normalizeIban(iban).toUpperCase();

/** Normalized IBAN set from loaded liquidity accounts (spaces stripped, uppercased). */
export const buildBankUploadAccountIbanSet = (
  accounts: readonly BankAccount[],
): ReadonlySet<string> =>
  new Set(
    accounts
      .filter(account => hasLinkableAccountIban(account.iban))
      .map(account => normalizeBankUploadAccountIban(account.iban)),
  );

export const isBankUploadIbanInAccountSet = (
  iban: unknown,
  allowedAccountIbans: ReadonlySet<string>,
): boolean => {
  if (isUndefined(iban) || typeof iban !== 'string') {
    return false;
  }

  const normalized = normalizeBankUploadAccountIban(iban);

  if (normalized === '') {
    return false;
  }

  return allowedAccountIbans.has(normalized);
};
